import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'
import { client } from '../Client'
import normalizeCallableCode from '../../helpers/callables/normalizeCallableCode'

/**
 * Haalt de registraties van één project op via een Cloud Function en zet ze om
 * naar documenten. Gedeeld door de kaarten op de projectpagina en de
 * portfoliokolommen: een koepel mag de collecties van zijn projecten niet zelf
 * lezen, de functie poort op lidmaatschap en projectrelatie.
 * AI-CONTEXT: See functions/portfolio/getRetentionSignals.js
 *
 * Zelfde teruggave als useFirestoreNoCompagnyWithStatus, zodat LastEntriesCard
 * hem als `source` kan gebruiken. `docs` is '' zolang er geen antwoord is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @template T
 * @param {string} callableName - Naam van de callable in europe-west1
 * @param {string} compagnyId - Het project; '' = nog onbekend
 * @param {(entry: object) => T} toDoc - Zet één teruggegeven entry om naar een document
 * @param {string} logLabel - Voorvoegsel voor de foutmelding in de console
 * @returns {{docs: Array<T> | '', error: {code?: string, message?: string} | null, loading: boolean}}
 */
const useProjectEntriesCall = (callableName, compagnyId, toDoc, logLabel) => {
    const [docs, setDocs] = useState(/** @type {Array<T> | ''} */ (''))
    const [error, setError] = useState(/** @type {{code?: string, message?: string} | null} */ (null))

    useEffect(() => {
        setDocs('')
        setError(null)
        if (!compagnyId) return undefined

        let active = true
        firebase.app().functions('europe-west1')
            .httpsCallable(callableName)({ client, compagnyId })
            .then((result) => {
                if (!active) return
                const entries = Array.isArray(result?.data?.entries) ? result.data.entries : []
                setDocs(entries.map(toDoc))
            })
            .catch((err) => {
                if (!active) return
                // eslint-disable-next-line no-console
                console.error(`[${logLabel}] ophalen mislukt`, err)
                setError({ code: normalizeCallableCode(err?.code), message: err?.message })
            })

        return () => { active = false }
        // toDoc en logLabel zijn per aanroeper vaste functies/teksten op moduleniveau.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callableName, compagnyId])

    return { docs, error, loading: !Array.isArray(docs) && !error }
}

/** De kaart en activityAge lezen `Timestamp.toDate()`; de functie stuurt ms. */
export const toTimestampLike = (/** @type {number|null} */ ms) => (ms === null || ms === undefined
    ? null
    : { toDate: () => new Date(ms) })

export default useProjectEntriesCall
